// Панель приглашения игроков в кампанию (используется и
// PreparationView, и CouncilTab). Вынесено из CampaignSetupPage.tsx
// без изменения поведения (Фаза 1).

import { useState, useEffect, useCallback, type FormEvent } from "react";
import { api, type CampaignSetup, type CampaignInviteCandidate } from "../api";
import { BlockHeader, type PageAction } from "./shared";

function InvitePlayersPanel({
  campaignId,
  setup,
  action,
}: {
  campaignId: string;
  setup: CampaignSetup;
  action: PageAction;
}) {
  const [telegramUser, setTelegramUser] = useState("");
  const [candidates, setCandidates] = useState<CampaignInviteCandidate[]>([]);
  const [candidateError, setCandidateError] = useState<string | null>(null);

  const loadCandidates = useCallback(async () => {
    try {
      setCandidateError(null);
      setCandidates(await api.inviteCandidates(campaignId));
    } catch (cause) {
      setCandidateError(
        cause instanceof Error
          ? cause.message
          : "Не удалось загрузить список игроков",
      );
    }
  }, [campaignId]);

  useEffect(() => {
    void loadCandidates();
  }, [loadCandidates, setup.pendingInvitations.length, setup.members.length]);

  async function inviteValue(value: string) {
    const normalized = value.trim();
    if (!normalized) return false;
    const ok = await action("Отправляем приглашение игроку…", () =>
      api.inviteCampaignUser(campaignId, normalized),
    );
    if (ok) {
      setTelegramUser("");
      await loadCandidates();
    }
    return ok;
  }
  async function invite(event: FormEvent) {
    event.preventDefault();
    await inviteValue(telegramUser);
  }
  async function toggleVisibility(next: boolean) {
    await action(
      next ? "Публикуем кампанию в поиске…" : "Скрываем кампанию из поиска…",
      () => api.updateCampaignVisibility(campaignId, next),
    );
  }
  return (
    <section className="cc-block cc-invite-panel">
      <BlockHeader
        eyebrow="Приглашения"
        title="Добавить игроков"
        icon="users"
      />
      <p className="cc-form-intro">
        Вход по коду остаётся доступен. Telegram не даёт Mini App прямой доступ
        к контактам пользователя, поэтому здесь показаны игроки, которые уже
        открывали City Chronicle. Ручной ввод @username или Telegram ID
        сохранён.
      </p>
      <label className="cc-check-toggle">
        <input
          type="checkbox"
          checked={setup.campaign.publicVisible}
          onChange={(event) => void toggleVisibility(event.target.checked)}
        />
        <span>Показывать кампанию в публичном поиске</span>
      </label>
      <form className="cc-inline-form cc-invite-form" onSubmit={invite}>
        <input
          value={telegramUser}
          onChange={(event) => setTelegramUser(event.target.value)}
          placeholder="@username или Telegram ID"
          maxLength={80}
        />
        <button
          type="submit"
          className="primary"
          disabled={!telegramUser.trim() || setup.campaign.memberCount >= 10}
        >
          Пригласить
        </button>
      </form>
      {candidates.length > 0 && (
        <div className="cc-invite-candidate-list">
          <strong>Быстрый выбор из игроков Mini App</strong>
          {candidates.map((candidate) => (
            <button
              type="button"
              key={candidate.userId}
              disabled={setup.campaign.memberCount >= 10}
              onClick={() =>
                void inviteValue(
                  candidate.username
                    ? `@${candidate.username}`
                    : `${candidate.telegramUserId}`,
                )
              }
            >
              <span>{candidate.displayName}</span>
              <small>
                {candidate.username
                  ? `@${candidate.username}`
                  : `ID ${candidate.telegramUserId}`}
              </small>
            </button>
          ))}
        </div>
      )}
      {candidateError && <small className="cc-muted">{candidateError}</small>}
      {candidates.length === 0 && !candidateError && (
        <small className="cc-muted">
          Быстрый выбор появится, когда другие пользователи хотя бы один раз
          откроют Mini App.
        </small>
      )}
      {setup.pendingInvitations.length > 0 && (
        <div className="cc-pending-invite-list">
          <strong>Ожидают ответа</strong>
          {setup.pendingInvitations.map((invitation) => (
            <span key={invitation.id}>
              {invitation.invitedUserDisplayName}
              {invitation.invitedUserUsername
                ? ` · @${invitation.invitedUserUsername}`
                : ""}
            </span>
          ))}
        </div>
      )}
      {setup.pendingInvitations.length === 0 && (
        <small className="cc-muted">Ожидающих приглашений нет.</small>
      )}
    </section>
  );
}

export {
  InvitePlayersPanel,
};
